import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { PageHeader, Panel, LoadingState, EmptyState } from "@/components/common";
import { DemoBadge } from "@/components/DemoBadge";
import { Briefcase, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export default function Portfolio() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("symbol");
  const [order, setOrder] = useState("asc");

  const { data, isLoading } = useQuery({
    queryKey: ["holdings", search, sortBy, order],
    queryFn: async () => (await api.get("/portfolio/holdings", { params: { search, sort_by: sortBy, order, page_size: 200 } })).data,
    placeholderData: keepPreviousData,
  });

  if (isLoading && !data) return <LoadingState />;

  const toggleSort = (c) => { if (sortBy === c) setOrder(order === "asc" ? "desc" : "asc"); else { setSortBy(c); setOrder("asc"); } };
  const totalPnl = Number(data.total_unrealized);
  const summary = [
    ["Invested", fmtMoney(data.total_invested), "text-slate-100"],
    ["Current Value", fmtMoney(data.total_current), "text-slate-100"],
    ["Unrealized P&L", fmtMoney(data.total_unrealized), totalPnl >= 0 ? "text-emerald-400" : "text-rose-400"],
  ];

  return (
    <div>
      <PageHeader title="Portfolio & Holdings" subtitle="Current demo positions and cost basis" icon={Briefcase} testId="portfolio-header" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        {summary.map(([k, v, tone]) => (
          <div key={k} className="rounded-lg border border-[var(--border-subtle)] bg-[var(--card)] p-4">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">{k}</div>
            <div className={cn("font-mono-num text-xl font-bold", tone)}>{v}</div>
          </div>
        ))}
      </div>

      <Panel title="Holdings" testId="holdings-panel" actions={<DemoBadge />}>
        <div className="p-3 border-b border-[var(--border-subtle)]">
          <div className="relative max-w-xs">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input data-testid="holdings-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search holdings..." className="h-8 pl-8 text-xs bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-slate-200" />
          </div>
        </div>
        {data.holdings.length === 0 ? (
          <EmptyState label="No holdings match" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="holdings-table">
              <thead className="bg-[var(--surface)]">
                <tr className="border-b border-[var(--border-subtle)] text-slate-400">
                  <th onClick={() => toggleSort("symbol")} className="px-3 py-2 text-left font-medium cursor-pointer hover:text-slate-200">Symbol</th>
                  <th className="px-3 py-2 text-left font-medium">Name</th>
                  <th className="px-3 py-2 text-left font-medium">Exchange</th>
                  <th className="px-3 py-2 text-left font-medium">Broker</th>
                  <th onClick={() => toggleSort("quantity")} className="px-3 py-2 text-right font-medium cursor-pointer hover:text-slate-200">Qty</th>
                  <th className="px-3 py-2 text-right font-medium">Avg Price</th>
                  <th className="px-3 py-2 text-right font-medium">LTP</th>
                  <th onClick={() => toggleSort("invested")} className="px-3 py-2 text-right font-medium cursor-pointer hover:text-slate-200">Invested</th>
                  <th className="px-3 py-2 text-right font-medium">Current</th>
                  <th onClick={() => toggleSort("unrealized_pnl")} className="px-3 py-2 text-right font-medium cursor-pointer hover:text-slate-200">P&L</th>
                  <th className="px-3 py-2 text-right font-medium">P&L %</th>
                  <th className="px-3 py-2 text-left font-medium">Recon</th>
                </tr>
              </thead>
              <tbody>
                {data.holdings.map((h) => {
                  const up = Number(h.unrealized_pnl) >= 0;
                  return (
                    <tr key={h.id} data-testid={`holding-row-${h.symbol}`} className="border-b border-[var(--border-subtle)] hover:bg-[var(--card-hover)]">
                      <td className="px-3 py-2.5 font-mono-num font-semibold text-slate-100">{h.symbol}</td>
                      <td className="px-3 py-2.5 text-slate-400 max-w-[160px] truncate">{h.name}</td>
                      <td className="px-3 py-2.5 text-slate-400">{h.exchange}</td>
                      <td className="px-3 py-2.5 text-slate-500 text-[10px]">{h.broker}</td>
                      <td className="px-3 py-2.5 text-right font-mono-num text-slate-300">{h.quantity}</td>
                      <td className="px-3 py-2.5 text-right font-mono-num text-slate-300">{fmtMoney(h.avg_price)}</td>
                      <td className="px-3 py-2.5 text-right font-mono-num text-slate-300">{fmtMoney(h.last_price)}</td>
                      <td className="px-3 py-2.5 text-right font-mono-num text-slate-400">{fmtMoney(h.invested)}</td>
                      <td className="px-3 py-2.5 text-right font-mono-num text-slate-200">{fmtMoney(h.current_value)}</td>
                      <td className={cn("px-3 py-2.5 text-right font-mono-num font-semibold", up ? "text-emerald-400" : "text-rose-400")}>{fmtMoney(h.unrealized_pnl)}</td>
                      <td className={cn("px-3 py-2.5 text-right font-mono-num", up ? "text-emerald-400" : "text-rose-400")}>{up ? "+" : ""}{h.unrealized_pct}%</td>
                      <td className="px-3 py-2.5"><span className="text-[10px] font-mono text-emerald-400">{h.reconciliation_status}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
